import React, {
    useEffect,
    useState,
} from "react";

import {
    FiArrowLeft,
    FiLoader,
    FiSave,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    clearSelectedSupplier,
    createSupplier,
    fetchSupplierById,
    selectedSupplierSelector,
    supplierErrorSelector,
    supplierLoadingSelector,
    updateSupplier,
} from "../../../../../../store/billing/suppliers/supplierSlice";

import "./Supplier.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
};

const getDateInputValue = (
    value
) => {
    if (!value) {
        return "";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    const year =
        date.getFullYear();

    const month =
        String(
            date.getMonth() + 1
        ).padStart(
            2,
            "0"
        );

    const day =
        String(
            date.getDate()
        ).padStart(
            2,
            "0"
        );

    return `${year}-${month}-${day}`;
};

const createEmptyAddress =
    () => ({
        addressLine1:
            "",

        addressLine2:
            "",

        city:
            "",

        state:
            "",

        stateCode:
            "",

        pincode:
            "",

        country:
            "India",
    });

const createEmptyBank =
    () => ({
        accountHolderName:
            "",

        bankName:
            "",

        accountNumber:
            "",

        ifscCode:
            "",

        branchName:
            "",

        upiId:
            "",
    });

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SupplierFormPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const {
        id,
    } =
        useParams();

    const isEdit =
        Boolean(id);

    /*
    |--------------------------------------------------------------------------
    | REDUX
    |--------------------------------------------------------------------------
    */

    const selectedSupplier =
        useSelector(
            selectedSupplierSelector
        );

    const loading =
        useSelector(
            supplierLoadingSelector
        ) || {};

    const errors =
        useSelector(
            supplierErrorSelector
        ) || {};

    /*
    |--------------------------------------------------------------------------
    | BASIC DETAILS
    |--------------------------------------------------------------------------
    */

    const [
        supplierName,
        setSupplierName,
    ] = useState("");

    const [
        companyName,
        setCompanyName,
    ] = useState("");

    const [
        contactPerson,
        setContactPerson,
    ] = useState("");

    const [
        mobile,
        setMobile,
    ] = useState("");

    const [
        alternateMobile,
        setAlternateMobile,
    ] = useState("");

    const [
        email,
        setEmail,
    ] = useState("");

    /*
    |--------------------------------------------------------------------------
    | TAX
    |--------------------------------------------------------------------------
    */

    const [
        gstin,
        setGstin,
    ] = useState("");

    const [
        pan,
        setPan,
    ] = useState("");

    const [
        gstRegistrationType,
        setGstRegistrationType,
    ] = useState(
        "UNREGISTERED"
    );

    /*
    |--------------------------------------------------------------------------
    | ADDRESS
    |--------------------------------------------------------------------------
    */

    const [
        billingAddress,
        setBillingAddress,
    ] = useState(
        createEmptyAddress()
    );

    const [
        shippingAddress,
        setShippingAddress,
    ] = useState(
        createEmptyAddress()
    );

    const [
        sameAsBillingAddress,
        setSameAsBillingAddress,
    ] = useState(true);

    /*
    |--------------------------------------------------------------------------
    | BANK
    |--------------------------------------------------------------------------
    */

    const [
        bankDetails,
        setBankDetails,
    ] = useState(
        createEmptyBank()
    );

    /*
    |--------------------------------------------------------------------------
    | PAYMENT
    |--------------------------------------------------------------------------
    */

    const [
        paymentTermDays,
        setPaymentTermDays,
    ] = useState(0);

    const [
        creditLimit,
        setCreditLimit,
    ] = useState(0);

    /*
    |--------------------------------------------------------------------------
    | OPENING BALANCE
    |--------------------------------------------------------------------------
    */

    const [
        openingBalance,
        setOpeningBalance,
    ] = useState(0);

    const [
        openingBalanceDate,
        setOpeningBalanceDate,
    ] = useState("");

    /*
    |--------------------------------------------------------------------------
    | NOTES
    |--------------------------------------------------------------------------
    */

    const [
        notes,
        setNotes,
    ] = useState("");

    const [
        localError,
        setLocalError,
    ] = useState("");

    /*
    |--------------------------------------------------------------------------
    | LOAD EDIT
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (!isEdit) {
                return undefined;
            }

            dispatch(
                fetchSupplierById(
                    id
                )
            );

            return () => {
                dispatch(
                    clearSelectedSupplier()
                );
            };
        },
        [
            dispatch,
            id,
            isEdit,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | POPULATE FORM
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            if (
                !isEdit ||
                !selectedSupplier
            ) {
                return;
            }

            setSupplierName(
                selectedSupplier
                    ?.supplierName ||
                ""
            );

            setCompanyName(
                selectedSupplier
                    ?.companyName ||
                ""
            );

            setContactPerson(
                selectedSupplier
                    ?.contactPerson ||
                ""
            );

            setMobile(
                selectedSupplier
                    ?.mobile ||
                ""
            );

            setAlternateMobile(
                selectedSupplier
                    ?.alternateMobile ||
                ""
            );

            setEmail(
                selectedSupplier
                    ?.email ||
                ""
            );

            setGstin(
                selectedSupplier
                    ?.gstin ||
                ""
            );

            setPan(
                selectedSupplier
                    ?.pan ||
                ""
            );

            setGstRegistrationType(
                selectedSupplier
                    ?.gstRegistrationType ||
                "UNREGISTERED"
            );

            setBillingAddress({
                ...createEmptyAddress(),

                ...(selectedSupplier
                    ?.billingAddress ||
                    {}),
            });

            setShippingAddress({
                ...createEmptyAddress(),

                ...(selectedSupplier
                    ?.shippingAddress ||
                    {}),
            });

            setSameAsBillingAddress(
                selectedSupplier
                    ?.sameAsBillingAddress !==
                false
            );

            setBankDetails({
                ...createEmptyBank(),

                ...(selectedSupplier
                    ?.bankDetails ||
                    {}),
            });

            setPaymentTermDays(
                selectedSupplier
                    ?.paymentTermDays ||
                0
            );

            setCreditLimit(
                selectedSupplier
                    ?.creditLimit ||
                0
            );

            setOpeningBalance(
                selectedSupplier
                    ?.openingBalance ||
                0
            );

            setOpeningBalanceDate(
                getDateInputValue(
                    selectedSupplier
                        ?.openingBalanceDate
                )
            );

            setNotes(
                selectedSupplier
                    ?.notes ||
                ""
            );
        },
        [
            isEdit,
            selectedSupplier,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | ADDRESS CHANGE
    |--------------------------------------------------------------------------
    */

    const handleBillingAddressChange = (
        field,
        value
    ) => {
        setBillingAddress(
            (
                current
            ) => ({
                ...current,

                [field]:
                    value,
            })
        );

        if (
            sameAsBillingAddress
        ) {
            setShippingAddress(
                (
                    current
                ) => ({
                    ...current,

                    [field]:
                        value,
                })
            );
        }
    };

    const handleShippingAddressChange = (
        field,
        value
    ) => {
        setShippingAddress(
            (
                current
            ) => ({
                ...current,

                [field]:
                    value,
            })
        );
    };

    /*
    |--------------------------------------------------------------------------
    | SAME ADDRESS
    |--------------------------------------------------------------------------
    */

    const handleSameAddressChange = (
        checked
    ) => {
        setSameAsBillingAddress(
            checked
        );

        if (checked) {
            setShippingAddress({
                ...billingAddress,
            });
        }
    };

    /*
    |--------------------------------------------------------------------------
    | BANK CHANGE
    |--------------------------------------------------------------------------
    */

    const handleBankChange = (
        field,
        value
    ) => {
        setBankDetails(
            (
                current
            ) => ({
                ...current,

                [field]:
                    value,
            })
        );
    };

    /*
    |--------------------------------------------------------------------------
    | GST
    |--------------------------------------------------------------------------
    */

    const handleGSTChange = (
        value
    ) => {
        const normalized =
            String(
                value || ""
            )
                .toUpperCase()
                .replace(
                    /\s/g,
                    ""
                );

        setGstin(
            normalized
        );

        if (
            normalized
        ) {
            if (
                gstRegistrationType ===
                "UNREGISTERED"
            ) {
                setGstRegistrationType(
                    "REGULAR"
                );
            }
        } else {
            setGstRegistrationType(
                "UNREGISTERED"
            );
        }
    };

    /*
    |--------------------------------------------------------------------------
    | VALIDATION
    |--------------------------------------------------------------------------
    */

    const validate =
        () => {
            const normalizedName =
                supplierName.trim();

            const normalizedMobile =
                String(
                    mobile || ""
                ).replace(
                    /\D/g,
                    ""
                );

            if (
                !normalizedName
            ) {
                return "Supplier name is required.";
            }

            if (
                !normalizedMobile
            ) {
                return "Supplier mobile number is required.";
            }

            if (
                normalizedMobile.length <
                10
            ) {
                return "Enter a valid supplier mobile number.";
            }

            if (
                email &&
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                    email
                )
            ) {
                return "Enter a valid email address.";
            }

            if (
                gstin &&
                gstin.length !==
                15
            ) {
                return "GSTIN must contain 15 characters.";
            }

            if (
                pan &&
                pan.length !==
                10
            ) {
                return "PAN must contain 10 characters.";
            }

            if (
                toNumber(
                    paymentTermDays
                ) < 0
            ) {
                return "Payment terms cannot be negative.";
            }

            if (
                toNumber(
                    creditLimit
                ) < 0
            ) {
                return "Credit limit cannot be negative.";
            }

            if (
                toNumber(
                    openingBalance
                ) < 0
            ) {
                return "Opening balance cannot be negative.";
            }

            if (
                toNumber(
                    openingBalance
                ) >
                0 &&
                !openingBalanceDate
            ) {
                return "Opening balance date is required.";
            }

            return "";
        };

    /*
    |--------------------------------------------------------------------------
    | PAYLOAD
    |--------------------------------------------------------------------------
    */

    const buildPayload =
        () => ({
            supplierName:
                supplierName.trim(),

            companyName:
                companyName.trim(),

            contactPerson:
                contactPerson.trim(),

            mobile:
                String(
                    mobile || ""
                ).replace(
                    /\D/g,
                    ""
                ),

            alternateMobile:
                String(
                    alternateMobile ||
                    ""
                ).replace(
                    /\D/g,
                    ""
                ),

            email:
                email
                    .trim()
                    .toLowerCase(),

            gstin:
                gstin
                    .trim()
                    .toUpperCase(),

            pan:
                pan
                    .trim()
                    .toUpperCase(),

            gstRegistrationType,

            billingAddress: {
                addressLine1:
                    billingAddress
                        .addressLine1
                        .trim(),

                addressLine2:
                    billingAddress
                        .addressLine2
                        .trim(),

                city:
                    billingAddress
                        .city
                        .trim(),

                state:
                    billingAddress
                        .state
                        .trim(),

                stateCode:
                    billingAddress
                        .stateCode
                        .trim(),

                pincode:
                    billingAddress
                        .pincode
                        .trim(),

                country:
                    billingAddress
                        .country
                        .trim() ||
                    "India",
            },

            shippingAddress:
                sameAsBillingAddress
                    ? {
                        ...billingAddress,
                    }
                    : {
                        ...shippingAddress,
                    },

            sameAsBillingAddress,

            bankDetails: {
                accountHolderName:
                    bankDetails
                        .accountHolderName
                        .trim(),

                bankName:
                    bankDetails
                        .bankName
                        .trim(),

                accountNumber:
                    bankDetails
                        .accountNumber
                        .trim(),

                ifscCode:
                    bankDetails
                        .ifscCode
                        .trim()
                        .toUpperCase(),

                branchName:
                    bankDetails
                        .branchName
                        .trim(),

                upiId:
                    bankDetails
                        .upiId
                        .trim(),
            },

            paymentTermDays:
                Math.max(
                    toNumber(
                        paymentTermDays
                    ),
                    0
                ),

            creditLimit:
                Math.max(
                    toNumber(
                        creditLimit
                    ),
                    0
                ),

            openingBalance:
                Math.max(
                    toNumber(
                        openingBalance
                    ),
                    0
                ),

            openingBalanceDate:
                openingBalanceDate ||
                null,

            notes:
                notes.trim(),
        });

    /*
    |--------------------------------------------------------------------------
    | SAVE
    |--------------------------------------------------------------------------
    */

    const handleSave =
        async () => {
            const validationError =
                validate();

            if (
                validationError
            ) {
                setLocalError(
                    validationError
                );

                return;
            }

            try {
                setLocalError("");

                let supplier =
                    null;

                if (isEdit) {
                    supplier =
                        await dispatch(
                            updateSupplier({
                                supplierId:
                                    id,

                                payload:
                                    buildPayload(),
                            })
                        ).unwrap();
                } else {
                    supplier =
                        await dispatch(
                            createSupplier(
                                buildPayload()
                            )
                        ).unwrap();
                }

                const supplierId =
                    supplier?._id ||
                    supplier
                        ?.data
                        ?._id;

                if (
                    supplierId
                ) {
                    history.push(
                        `/billing/suppliers/${supplierId}`
                    );
                } else {
                    history.push(
                        "/billing/suppliers"
                    );
                }
            } catch (
            error
            ) {
                setLocalError(
                    typeof error ===
                        "string"
                        ? error
                        : error?.message ||
                        "Unable to save supplier."
                );
            }
        };

    /*
    |--------------------------------------------------------------------------
    | CANCEL
    |--------------------------------------------------------------------------
    */

    const handleCancel =
        () => {
            if (
                isEdit &&
                id
            ) {
                history.push(
                    `/billing/suppliers/${id}`
                );

                return;
            }

            history.push(
                "/billing/suppliers"
            );
        };

    const saving =
        loading.create ||
        loading.update;

    /*
    |--------------------------------------------------------------------------
    | OPENING BALANCE LOCK
    |--------------------------------------------------------------------------
    */

    const openingBalanceLocked =
        Boolean(
            isEdit &&
            selectedSupplier
                ?.openingBalancePosted
        );

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="supplier-page">
            {/* HEADER */}

            <header className="supplier-header">
                <div className="supplier-heading">
                    <button
                        type="button"
                        className="supplier-icon-button"
                        onClick={
                            handleCancel
                        }
                    >
                        <FiArrowLeft />
                    </button>

                    <div>
                        <small>
                            SUPPLIER MASTER
                        </small>

                        <h1>
                            {isEdit
                                ? "Edit Supplier"
                                : "Add Supplier"}
                        </h1>

                        <p>
                            Manage supplier contact, tax, address, payment and bank information.
                        </p>
                    </div>
                </div>

                <div className="supplier-header-actions">
                    <button
                        type="button"
                        className="supplier-button"
                        disabled={
                            saving
                        }
                        onClick={
                            handleCancel
                        }
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        className="supplier-button supplier-button-primary"
                        disabled={
                            saving
                        }
                        onClick={
                            handleSave
                        }
                    >
                        {saving ? (
                            <FiLoader className="supplier-spinner" />
                        ) : (
                            <FiSave />
                        )}

                        {isEdit
                            ? "Update Supplier"
                            : "Save Supplier"}
                    </button>
                </div>
            </header>

            {/* ERROR */}

            {(localError ||
                errors.create ||
                errors.update ||
                errors.details) && (
                    <div className="supplier-alert">
                        {localError ||
                            errors.create ||
                            errors.update ||
                            errors.details}
                    </div>
                )}

            {isEdit &&
                loading.details &&
                !selectedSupplier ? (
                <div className="supplier-empty">
                    <FiLoader className="supplier-spinner" />

                    <h3>
                        Loading supplier...
                    </h3>
                </div>
            ) : (
                <>
                    {/* BASIC DETAILS */}

                    <section className="supplier-form-card">
                        <div className="supplier-section-title">
                            <h3>
                                Basic Details
                            </h3>

                            <p>
                                Supplier identity and contact information.
                            </p>
                        </div>

                        <div className="supplier-form-grid">
                            <div className="supplier-form-field">
                                <label>
                                    Supplier Name *
                                </label>

                                <input
                                    type="text"
                                    value={
                                        supplierName
                                    }
                                    placeholder="Supplier name"
                                    onChange={(
                                        event
                                    ) =>
                                        setSupplierName(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Company Name
                                </label>

                                <input
                                    type="text"
                                    value={
                                        companyName
                                    }
                                    placeholder="Business / company name"
                                    onChange={(
                                        event
                                    ) =>
                                        setCompanyName(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Contact Person
                                </label>

                                <input
                                    type="text"
                                    value={
                                        contactPerson
                                    }
                                    placeholder="Contact person"
                                    onChange={(
                                        event
                                    ) =>
                                        setContactPerson(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Mobile *
                                </label>

                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength="15"
                                    value={
                                        mobile
                                    }
                                    placeholder="Mobile number"
                                    onChange={(
                                        event
                                    ) =>
                                        setMobile(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Alternate Mobile
                                </label>

                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength="15"
                                    value={
                                        alternateMobile
                                    }
                                    placeholder="Alternate mobile"
                                    onChange={(
                                        event
                                    ) =>
                                        setAlternateMobile(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Email
                                </label>

                                <input
                                    type="email"
                                    value={
                                        email
                                    }
                                    placeholder="supplier@email.com"
                                    onChange={(
                                        event
                                    ) =>
                                        setEmail(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </section>

                    {/* TAX */}

                    <section className="supplier-form-card">
                        <div className="supplier-section-title">
                            <h3>
                                GST & Tax Details
                            </h3>

                            <p>
                                Supplier GST registration and PAN details.
                            </p>
                        </div>

                        <div className="supplier-form-grid">
                            <div className="supplier-form-field">
                                <label>
                                    GST Registration
                                </label>

                                <select
                                    value={
                                        gstRegistrationType
                                    }
                                    onChange={(
                                        event
                                    ) => {
                                        const value =
                                            event
                                                .target
                                                .value;

                                        setGstRegistrationType(
                                            value
                                        );

                                        if (
                                            value ===
                                            "UNREGISTERED"
                                        ) {
                                            setGstin(
                                                ""
                                            );
                                        }
                                    }}
                                >
                                    <option value="UNREGISTERED">
                                        Unregistered
                                    </option>

                                    <option value="REGULAR">
                                        Regular
                                    </option>

                                    <option value="COMPOSITION">
                                        Composition
                                    </option>
                                </select>
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    GSTIN
                                </label>

                                <input
                                    type="text"
                                    maxLength="15"
                                    disabled={
                                        gstRegistrationType ===
                                        "UNREGISTERED"
                                    }
                                    value={
                                        gstin
                                    }
                                    placeholder="22AAAAA0000A1Z5"
                                    onChange={(
                                        event
                                    ) =>
                                        handleGSTChange(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    PAN
                                </label>

                                <input
                                    type="text"
                                    maxLength="10"
                                    value={
                                        pan
                                    }
                                    placeholder="ABCDE1234F"
                                    onChange={(
                                        event
                                    ) =>
                                        setPan(
                                            event
                                                .target
                                                .value
                                                .toUpperCase()
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </section>

                    {/* BILLING ADDRESS */}

                    <section className="supplier-form-card">
                        <div className="supplier-section-title">
                            <h3>
                                Billing Address
                            </h3>
                        </div>

                        <div className="supplier-form-grid">
                            <div className="supplier-form-field supplier-form-field-wide">
                                <label>
                                    Address Line 1
                                </label>

                                <input
                                    type="text"
                                    value={
                                        billingAddress
                                            .addressLine1
                                    }
                                    placeholder="Building / street"
                                    onChange={(
                                        event
                                    ) =>
                                        handleBillingAddressChange(
                                            "addressLine1",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field supplier-form-field-wide">
                                <label>
                                    Address Line 2
                                </label>

                                <input
                                    type="text"
                                    value={
                                        billingAddress
                                            .addressLine2
                                    }
                                    placeholder="Area / landmark"
                                    onChange={(
                                        event
                                    ) =>
                                        handleBillingAddressChange(
                                            "addressLine2",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    City
                                </label>

                                <input
                                    type="text"
                                    value={
                                        billingAddress
                                            .city
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleBillingAddressChange(
                                            "city",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    State
                                </label>

                                <input
                                    type="text"
                                    value={
                                        billingAddress
                                            .state
                                    }
                                    placeholder="Rajasthan"
                                    onChange={(
                                        event
                                    ) =>
                                        handleBillingAddressChange(
                                            "state",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    State Code
                                </label>

                                <input
                                    type="text"
                                    value={
                                        billingAddress
                                            .stateCode
                                    }
                                    placeholder="08"
                                    onChange={(
                                        event
                                    ) =>
                                        handleBillingAddressChange(
                                            "stateCode",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Pincode
                                </label>

                                <input
                                    type="text"
                                    inputMode="numeric"
                                    value={
                                        billingAddress
                                            .pincode
                                    }
                                    placeholder="302017"
                                    onChange={(
                                        event
                                    ) =>
                                        handleBillingAddressChange(
                                            "pincode",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Country
                                </label>

                                <input
                                    type="text"
                                    value={
                                        billingAddress
                                            .country
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleBillingAddressChange(
                                            "country",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </section>

                    {/* SHIPPING ADDRESS */}

                    <section className="supplier-form-card">
                        <div className="supplier-section-title supplier-section-title-row">
                            <div>
                                <h3>
                                    Shipping Address
                                </h3>

                                <p>
                                    Optional separate delivery address.
                                </p>
                            </div>

                            <label className="supplier-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={
                                        sameAsBillingAddress
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleSameAddressChange(
                                            event
                                                .target
                                                .checked
                                        )
                                    }
                                />

                                Same as billing address
                            </label>
                        </div>

                        {!sameAsBillingAddress && (
                            <div className="supplier-form-grid">
                                <div className="supplier-form-field supplier-form-field-wide">
                                    <label>
                                        Address Line 1
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            shippingAddress
                                                .addressLine1
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleShippingAddressChange(
                                                "addressLine1",
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                <div className="supplier-form-field supplier-form-field-wide">
                                    <label>
                                        Address Line 2
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            shippingAddress
                                                .addressLine2
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleShippingAddressChange(
                                                "addressLine2",
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                <div className="supplier-form-field">
                                    <label>
                                        City
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            shippingAddress
                                                .city
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleShippingAddressChange(
                                                "city",
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                <div className="supplier-form-field">
                                    <label>
                                        State
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            shippingAddress
                                                .state
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleShippingAddressChange(
                                                "state",
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                <div className="supplier-form-field">
                                    <label>
                                        State Code
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            shippingAddress
                                                .stateCode
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleShippingAddressChange(
                                                "stateCode",
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                <div className="supplier-form-field">
                                    <label>
                                        Pincode
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            shippingAddress
                                                .pincode
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleShippingAddressChange(
                                                "pincode",
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>

                                <div className="supplier-form-field">
                                    <label>
                                        Country
                                    </label>

                                    <input
                                        type="text"
                                        value={
                                            shippingAddress
                                                .country
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleShippingAddressChange(
                                                "country",
                                                event
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </section>

                    {/* PAYMENT */}

                    <section className="supplier-form-card">
                        <div className="supplier-section-title">
                            <h3>
                                Payment Details
                            </h3>

                            <p>
                                Default supplier payment and credit terms.
                            </p>
                        </div>

                        <div className="supplier-form-grid">
                            <div className="supplier-form-field">
                                <label>
                                    Payment Term Days
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    value={
                                        paymentTermDays
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setPaymentTermDays(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Credit Limit
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={
                                        creditLimit
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setCreditLimit(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Opening Balance
                                </label>

                                <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    disabled={
                                        openingBalanceLocked
                                    }
                                    value={
                                        openingBalance
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setOpeningBalance(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />

                                {openingBalanceLocked && (
                                    <small>
                                        Opening balance is already posted to Supplier Ledger and cannot be edited directly.
                                    </small>
                                )}
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Opening Balance Date
                                </label>

                                <input
                                    type="date"
                                    disabled={
                                        openingBalanceLocked
                                    }
                                    value={
                                        openingBalanceDate
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        setOpeningBalanceDate(
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </section>

                    {/* BANK */}

                    <section className="supplier-form-card">
                        <div className="supplier-section-title">
                            <h3>
                                Bank Details
                            </h3>
                        </div>

                        <div className="supplier-form-grid">
                            <div className="supplier-form-field">
                                <label>
                                    Account Holder Name
                                </label>

                                <input
                                    type="text"
                                    value={
                                        bankDetails
                                            .accountHolderName
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleBankChange(
                                            "accountHolderName",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Bank Name
                                </label>

                                <input
                                    type="text"
                                    value={
                                        bankDetails
                                            .bankName
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleBankChange(
                                            "bankName",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Account Number
                                </label>

                                <input
                                    type="text"
                                    value={
                                        bankDetails
                                            .accountNumber
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleBankChange(
                                            "accountNumber",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    IFSC Code
                                </label>

                                <input
                                    type="text"
                                    value={
                                        bankDetails
                                            .ifscCode
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleBankChange(
                                            "ifscCode",
                                            event
                                                .target
                                                .value
                                                .toUpperCase()
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    Branch
                                </label>

                                <input
                                    type="text"
                                    value={
                                        bankDetails
                                            .branchName
                                    }
                                    onChange={(
                                        event
                                    ) =>
                                        handleBankChange(
                                            "branchName",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>

                            <div className="supplier-form-field">
                                <label>
                                    UPI ID
                                </label>

                                <input
                                    type="text"
                                    value={
                                        bankDetails
                                            .upiId
                                    }
                                    placeholder="example@upi"
                                    onChange={(
                                        event
                                    ) =>
                                        handleBankChange(
                                            "upiId",
                                            event
                                                .target
                                                .value
                                        )
                                    }
                                />
                            </div>
                        </div>
                    </section>

                    {/* NOTES */}

                    <section className="supplier-form-card">
                        <div className="supplier-section-title">
                            <h3>
                                Notes
                            </h3>
                        </div>

                        <div className="supplier-form-field">
                            <textarea
                                rows="4"
                                value={
                                    notes
                                }
                                placeholder="Supplier notes..."
                                onChange={(
                                    event
                                ) =>
                                    setNotes(
                                        event
                                            .target
                                            .value
                                    )
                                }
                            />
                        </div>
                    </section>
                </>
            )}
        </div>
    );
};

export default SupplierFormPage;